import { useState } from "react"
import { createPortal } from "react-dom"
import classNames from "classnames"

import { useAppSelector } from "store/hooks"
import { ApiTypes } from "types"
import { MonitoringReportAnnotations } from "types/MonitoringReportAnnotations"

import { ModalLayout } from "layouts/ModalLayout"
import ModalSectionTitle from "components/common/ModalSectionTitle/ModalSectionTitle"
import { TemperatureToggle } from "components/form/TemperatureToggle"
import CustomerNameContainer from "components/table/columnComponents/CustomerName/CustomerNameContainer"

import LiveDateView from "./LiveDataView"
import { StatsReportChartsWrapper } from "./StatsReportChartsWrapper"

import styles from "./index.module.scss"

interface Props {
  device: ApiTypes.Model.Device
  monitoringReportAnnotations?: MonitoringReportAnnotations | null
  isVendor?: boolean
  onClearMonitoringReportAnnotations?: () => void
  onCancel: () => void
  defaultPeriod?: string
  defaultTab?: "liveData" | "reporting"
}

export const StatsReportModal = ({
  device,
  monitoringReportAnnotations,
  isVendor,
  onClearMonitoringReportAnnotations,
  onCancel,
  defaultPeriod,
  defaultTab,
}: Props) => {
  const [activeTab, setActiveTab] = useState(defaultTab || (monitoringReportAnnotations ? "reporting" : "liveData"))
  const userTemperatureScale = useAppSelector(state => state.user.userTemperatureScale)
  const isCelsius = userTemperatureScale === "celsius"

  if (!device) return null

  return (
    <>
      {createPortal(
        <ModalLayout buttonSize="auto" onClose={onCancel} isDrawer isWithoutFooter>
          <div className={styles.modalContent}>
            <h3 className={styles.modalTitle}>Stats Report</h3>
          </div>
          <div className={styles.tempToggleWrapper}>
            <TemperatureToggle />
          </div>
          <ModalSectionTitle title="Details" />
          <div className={styles.statsScrollableArea}>
            <div className={classNames(styles.monitoringReportContent)}>
              <div className={classNames(styles.devicesDataContainer)}>
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Customer</h4>
                  <div className={styles.devicesDataText}>
                    <CustomerNameContainer
                      id={device?.client_id}
                      name={device?.homeowner_name}
                      isInstalled={true}
                      deviceSN={device?.sn}
                      showDashOnEmptyName
                      isVendor={isVendor}
                    />
                  </div>
                </div>
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Zip Code</h4>
                  <div className={styles.devicesDataText}>{device?.zip?.code || "-"}</div>
                </div>
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Email</h4>
                  <div className={styles.devicesDataText}>{device?.email || "-"}</div>
                </div>
                <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Thermostat S/N</h4>
                  <div className={styles.devicesDataText}>{device?.sn}</div>
                </div>
                {/* <div className={styles.devicesDataItem}>
                  <h4 className={styles.modalSubtitle}>Mobile App</h4>
                  <div className={styles.devicesDataText}>Activated</div>
                </div> */}
              </div>
            </div>
            <div className={styles.drawerTabsWrapper}>
              <div className={styles.drawerTabs}>
                <div
                  className={classNames(styles.drawerTab, { [styles.active]: activeTab === "liveData" })}
                  onClick={() => setActiveTab("liveData")}
                >
                  Live Data
                </div>
                <div
                  className={classNames(styles.drawerTab, { [styles.active]: activeTab === "reporting" })}
                  onClick={() => setActiveTab("reporting")}
                >
                  Reporting
                </div>
              </div>
            </div>
            {activeTab === "liveData" ? (
              <LiveDateView device={device} isCelsius={isCelsius} />
            ) : (
              <StatsReportChartsWrapper
                device={device}
                isCelsius={isCelsius}
                monitoringReportAnnotations={monitoringReportAnnotations}
                onClearMonitoringReportAnnotations={onClearMonitoringReportAnnotations}
                defaultPeriod={defaultPeriod}
              />
            )}
          </div>
        </ModalLayout>,
        document.body,
      )}
    </>
  )
}
